<template>
    <AppContainer>
        <AppBreadcrumb :items="breadcrumbItems" />
        <el-row class="align-center">
            <h1>File usages</h1>
        </el-row>
        <SearchArea
            label="file usages"
            :pagination-data="paginationData"
            :headers="headers"
            :query="query"
            :loading="isLoading"
            :action="false"
            @search="handleClickSearch"
            @size-change="handleClickSizeChange"
            @current-change="handleClickCurrentChange"
            @sort="handleClickSort"
        >
            <template #searchInputs>
                <el-row :gutter="10" class="app-search">
                    <el-col :sm="6">
                        <el-date-picker
                            v-model="searchQuery.date"
                            type="month"
                            format="YYYY-MM"
                            value-format="YYYY-MM"
                            placeholder="Date"
                            @change="handleClickSearch"
                        ></el-date-picker>
                    </el-col>
                    <el-col :sm="6">
                        <el-select
                            v-model="searchQuery.fileType"
                            clearable
                            placeholder="File Type"
                            class="mr-4"
                            @change="handleClickSearch"
                        >
                            <el-option
                                :key="0"
                                label="pdf"
                                value="pdf"
                            ></el-option>
                            <el-option
                                :key="1"
                                label="docx"
                                value="docx"
                            ></el-option>
                        </el-select>
                    </el-col>
                    <el-col :sm="6">
                        <el-input
                            v-model="searchQuery.organizationName"
                            clearable
                            placeholder="Organization name"
                            class="mr-4"
                            @change="handleClickSearch"
                        />
                    </el-col>
                    <el-col :sm="6">
                        <el-input
                            v-model="searchQuery.licenseKey"
                            clearable
                            placeholder="License key"
                            class="mr-4"
                            @change="handleClickSearch"
                        />
                    </el-col>
                </el-row>
            </template>
        </SearchArea>
    </AppContainer>
</template>

<script lang="ts">
import SearchArea from '@/components/SearchArea.vue';
import {
    DEFAULT_QUERY,
    QueryPaginate,
    SortEvent,
} from '@/types/PaginationData';
import { defineComponent, reactive, computed, toRefs } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AppBreadcrumb from '@/components/AppBreadcrumb.vue';
import AppContainer from '@/components/AppContainer.vue';
import { useStore } from 'vuex';
import { SearchFileUsages } from '@/types/FileUsages';

export default defineComponent({
    components: { AppContainer, AppBreadcrumb, SearchArea },
    setup() {
        const router = useRouter();
        const store = useStore();
        const data = reactive({
            breadcrumbItems: [
                {
                    text: 'Index',
                    to: '/',
                    exact: true,
                },
                { text: 'Api usages' },
            ],
            headers: [
                {
                    text: 'License ID',
                    value: 'licenseId',
                    selected: true,
                    sortable: true,
                },
                {
                    text: 'License key',
                    value: 'licenseKey',
                    selected: true,
                    sortable: false,
                },
                {
                    text: 'Organization Name',
                    value: 'organizationName',
                    selected: true,
                    sortable: true,
                },
                {
                    text: 'File Type',
                    value: 'fileType',
                    selected: true,
                    sortable: true,
                },
                {
                    text: 'Total File Size',
                    value: 'totalFilesSize',
                    selected: true,
                    sortable: true,
                },
                {
                    text: 'Date',
                    value: 'date',
                    sortable: true,
                    selected: true,
                },
            ],
            searchQuery: {
                date: '',
                organizationName: '',
                licenseKey: '',
                fileType: undefined,
            } as SearchFileUsages,
            paginationData: computed(() => {
                return store.getters['fileUsages/paginationData'];
            }),
            isLoading: computed(() => {
                return store.state.fileUsages.isLoading;
            }),
            query: computed(() => {
                return store.state.fileUsages.query;
            }),
        });

        const handleClickSearch = () => {
            store.dispatch('fileUsages/setSearch', data.searchQuery);
            router.replace({ query: store.state.fileUsages.query as any });
        };

        const handleClickSort = (event: SortEvent) => {
            store.dispatch('fileUsages/setSort', event);
        };

        const handleClickSizeChange = (limit: number) => {
            const event: QueryPaginate = {
                limit: limit,
                offset: 0,
            };
            store.dispatch('fileUsages/setPaginate', event);
            router.replace({ query: store.state.fileUsages.query as any });
        };

        const handleClickCurrentChange = (event: QueryPaginate) => {
            store.dispatch('fileUsages/setPaginate', event);
            router.replace({
                query: store.state.fileUsages.query as any,
            });
        };

        return {
            ...toRefs(data),
            handleClickSearch,
            handleClickSort,
            handleClickSizeChange,
            handleClickCurrentChange,
        };
    },
    created() {
        const router = useRouter();
        const store = useStore();
        store.dispatch('fileUsages/setSearch');
        router.replace({ query: store.state.fileUsages.query as any });
    },
});
</script>
